// Page setup for the internal report PDFs (James 2026-10-29): Margins
// (Narrow 0.4" / Moderate 0.5" / Wide 0.75") and Table size (Smaller /
// Normal / Larger) on US Letter.  The geometry itself lives in lib/pdf.js
// (pdfApplyPageSetup, pdfPageGeometry); this module decides WHICH setup a
// person gets:
//
//   1. their own choice, made beside Portrait/Landscape on a report and
//      remembered per person on this computer
//      (localStorage 'lextrack_report_page_setup:<user id>');
//   2. else the firm default (Reports → ⚙️ PDF Style: firm_settings
//      report_page_margins / report_table_size);
//   3. else Narrow / Normal.
//
// One choice covers every report (margins are a printing preference, and the
// table fitter still steps a too-wide table down).  Storage can throw
// (private window, blocked site data): reads then fall back to the firm
// default and a save lasts for this visit only.  Registered in lib/uiPrefs.js
// ("Sort + filter preferences"); another person's choice on the same
// computer is theirs and is never listed or cleared.

import {
  PDF_MARGIN_CHOICES, PDF_TABLE_SIZE_CHOICES, PDF_DEFAULT_MARGINS, PDF_DEFAULT_TABLE_SIZE,
} from './pdf'

export const PDF_PAGE_SETUP_KEY = 'lextrack_report_page_setup'
export const PDF_PAGE_SETUP_EVENT = 'lextrack:pdf-page-setup'

export const pdfPageSetupKeyFor = (uid) => `${PDF_PAGE_SETUP_KEY}:${uid}`

const MARGIN_IDS = new Set(PDF_MARGIN_CHOICES.map(c => c.id))
const SIZE_IDS = new Set(PDF_TABLE_SIZE_CHOICES.map(c => c.id))
const validMargins = (v) => (MARGIN_IDS.has(v) ? v : null)
const validSize = (v) => (SIZE_IDS.has(v) ? v : null)

// This visit's choices when storage is unavailable, per user id.
const sessionChoices = new Map()
// A choice whose save failed although storage can be read (quota full,
// blocked writes): it wins over the stored value for this visit, until a save
// succeeds or "Reset UI preferences" clears it (forgetPendingPdfPageSetup).
const pendingChoices = new Map()

const hasUser = (uid) => uid !== null && uid !== undefined && String(uid) !== ''

export function readPersonalPdfPageSetup (uid) {
  if (!hasUser(uid)) return {}
  // This visit's copy only when storage itself is unavailable: once storage
  // works, an absent key means "no choice" (e.g. after Reset UI preferences).
  let raw = null
  let storageOk = true
  try { raw = window.localStorage.getItem(pdfPageSetupKeyFor(uid)) } catch { storageOk = false }
  let parsed = null
  if (raw) { try { parsed = JSON.parse(raw) } catch { parsed = null } }
  const pending = pendingChoices.get(String(uid))
  const src = pending || (storageOk ? (parsed && typeof parsed === 'object' ? parsed : {}) : (sessionChoices.get(String(uid)) || {}))
  const out = {}
  if (validMargins(src.margins)) out.margins = src.margins
  if (validSize(src.tableSize)) out.tableSize = src.tableSize
  return out
}

// The firm default from GET /api/settings (either key may be missing while
// settings load, or on a server without the columns yet).
export function firmPdfPageSetup (firmSettings) {
  const fs = firmSettings || {}
  return {
    margins: validMargins(fs.report_page_margins) || PDF_DEFAULT_MARGINS,
    tableSize: validSize(fs.report_table_size) || PDF_DEFAULT_TABLE_SIZE,
  }
}

/**
 * { margins, tableSize, personal: { margins?, tableSize? }, firm: { margins,
 * tableSize } } for this person.  `firm` is what "Firm default" in the
 * pickers names — the firm's choice even when the person has their own.
 */
export function resolvePdfPageSetup ({ firmSettings, userId } = {}) {
  const firm = firmPdfPageSetup(firmSettings)
  const personal = readPersonalPdfPageSetup(userId)
  return {
    margins: personal.margins || firm.margins,
    tableSize: personal.tableSize || firm.tableSize,
    personal,
    firm,
  }
}

/**
 * Remember this person's choice ({ margins } and/or { tableSize }; null
 * forgets that part, so the firm default applies again).  Fires
 * PDF_PAGE_SETUP_EVENT in this window; other windows hear the storage event.
 */
export function savePersonalPdfPageSetup (uid, patch = {}) {
  if (!hasUser(uid)) return {}
  const next = { ...readPersonalPdfPageSetup(uid) }
  if ('margins' in patch) { if (validMargins(patch.margins)) next.margins = patch.margins; else delete next.margins }
  if ('tableSize' in patch) { if (validSize(patch.tableSize)) next.tableSize = patch.tableSize; else delete next.tableSize }
  sessionChoices.set(String(uid), next)
  try {
    if (Object.keys(next).length) window.localStorage.setItem(pdfPageSetupKeyFor(uid), JSON.stringify(next))
    else window.localStorage.removeItem(pdfPageSetupKeyFor(uid))
    pendingChoices.delete(String(uid))
  } catch { pendingChoices.set(String(uid), next) /* kept for this visit only */ }
  try { window.dispatchEvent(new CustomEvent(PDF_PAGE_SETUP_EVENT, { detail: { uid: String(uid) } })) } catch { /* no window events (tests) */ }
  return next
}

// "Reset UI preferences" (lib/uiPrefs.js) clears storage; a choice whose save
// had failed must go too.
export function forgetPendingPdfPageSetup () {
  pendingChoices.clear()
  sessionChoices.clear()
}
